'use client'

import { useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { ExplanationAccessCodeForm } from '@/Explanation/ExplanationAccessCodeForm'

type ExplanationPasswordLookupProps = {
  title: string
}

export default function ExplanationPasswordLookup({
  title,
}: ExplanationPasswordLookupProps) {
  const router = useRouter()
  const searchParams = useSearchParams()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<{ accessCode: string }>({
    defaultValues: {
      accessCode: '',
    },
  })

  const onSubmit = async (data: { accessCode: string }) => {
    setIsSubmitting(true)
    try {
      const res = await fetch('/api/explanation/lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: data.accessCode }),
      })

      const json = (await res.json()) as { slug?: string; error?: string }

      if (!res.ok) {
        setError('root', {
          type: 'manual',
          message: json.error || 'Incorrect access code',
        })
        return
      }

      const slug = json.slug?.trim()

      if (!slug) {
        setError('root', {
          type: 'manual',
          message: 'Incorrect access code',
        })
        return
      }

      const nextParams = new URLSearchParams(searchParams?.toString() ?? '')
      // Keep any "file" selection if it was present (password is NOT put in the URL)
      const query = nextParams.toString()

      router.push(
        `/explanation/${encodeURIComponent(slug)}${query ? `?${query}` : ''}`
      )
    } catch (err) {
      setError('root', {
        type: 'manual',
        message: err instanceof Error ? err.message : 'Incorrect access code',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ExplanationAccessCodeForm
      heading="Access your explanation"
      title={title}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      inputPlaceholder="Enter password"
      submitLabel={isSubmitting ? 'Validating...' : 'Submit password'}
      errors={
        Object.values(errors)
          .map(error => error?.message)
          .filter(Boolean) as string[]
      }
      inputProps={register('accessCode', {
        required: 'Access code is required',
      })}
    />
  )
}
